import { useReactiveVar } from '@apollo/client';
import React from 'react';
import { vars } from '../../../../reactive';
import { Slot } from '../../../../reactive/main';
import { THREE_DAYS, areDatesEqual, getBiggestAndSmallestDates, isDateInBetween, toDateString } from '../../helpers';
import { FORM_RADIO_COMPONENT_TYPES, FORM_RADIO_TYPES, FormDateAndTime } from '../../types';
import FormRadioDateUntilInput from './FormRadioDateUntilInput';
import { BookingButton, BookingButtonBackgroud, BookingButtonContainer, CalendarDayLabel, FormDateLabel } from './styled';

const FormRadioDateButton = ({
  formOptions,
  booking,
  bookingMonth,
  bookings,
  loading,
  watchedDates,
  session,
  type,
  checkIfAvailable,
  selectedSlots
}: {
  formOptions: FormDateAndTime;
  booking: any;
  bookingMonth: string;
  bookings: Record<string, any>;
  loading: boolean;
  watchedDates: (string | Date)[][] | (string | Date)[];
  session: number;
  type: keyof typeof FORM_RADIO_TYPES;
  checkIfAvailable: (fullDate: Date) => boolean;
  selectedSlots: Slot[];
}) => {
  const { registerAs, requiredMessage, errorMessage } = FORM_RADIO_COMPONENT_TYPES[type];
  const { fullDate, day, date } = booking;
  const isMultiday = type === FORM_RADIO_TYPES.DATE_UNTIL;
  const isCheckoutOnly = isMultiday && booking.slots?.[0]?.isCheckoutOnly;
  const isDisabled = loading || (!checkIfAvailable(fullDate) && !isCheckoutOnly);
  const selectedProduct = useReactiveVar(vars.selectedProduct);
  const bookingsInMonth = bookings[bookingMonth];
  const lastDayOfMonth = bookingsInMonth?.[bookingsInMonth?.length - 1];
  const firstDayOfMonth = bookingsInMonth?.[0];
  const dates = getBiggestAndSmallestDates(watchedDates[session] as (string | Date)[]);
  const selectedDuration = type === FORM_RADIO_TYPES.DATE_UNTIL && isDateInBetween(dates?.smallestDate, dates?.biggestDate, fullDate);
  const isFirstSelected = areDatesEqual(dates?.smallestDate, fullDate);
  const isLastSelected = areDatesEqual(dates?.biggestDate, fullDate);
  const isLastDayOfMonth = areDatesEqual(lastDayOfMonth?.fullDate, fullDate);
  const isFirstDayOfMonth = areDatesEqual(firstDayOfMonth?.fullDate, fullDate);
  const isLastDayInRow = booking?.day === THREE_DAYS[0];
  const isFirstDayInRow = booking?.day === THREE_DAYS[1];
  const { register } = formOptions;
  const allSessionsFilled = selectedSlots.every(({ date, startDate, days }) => date?.fullDate || startDate?.fullDate || days?.length) && selectedSlots.length === selectedProduct?.sessions;

  return (
    <FormDateLabel isLastDayInRow={isLastDayInRow} isMultiday={isMultiday}>
      {!isMultiday && (
        <input
          {...register(`${registerAs}.${session}`, {
            validate: (value: (typeof booking)['fullDate']) => {
              if (!checkIfAvailable(value)) {
                return errorMessage;
              }

              if (!allSessionsFilled) {
                return requiredMessage;
              }
            }
          })}
          type="radio"
          value={toDateString(fullDate)}
          disabled={isDisabled}
        />
      )}

      {isMultiday && (
        <FormRadioDateUntilInput
          type={type}
          formOptions={formOptions}
          checkIfAvailable={checkIfAvailable}
          loading={loading}
          booking={booking}
          watchedDates={watchedDates as (string | Date)[][]}
          session={session}
          bookings={bookings}
        />
      )}
      <BookingButtonContainer
        isMultiday={isMultiday}
        selectedDuration={selectedDuration}
        firstDay={isFirstSelected || isFirstDayOfMonth}
        lastDay={isLastSelected || isLastDayOfMonth || isLastDayInRow}
        isLastDayInRow={isLastDayInRow}
        isFirstDayInRow={isFirstDayInRow}
      >
        {!isMultiday && <CalendarDayLabel>{day}</CalendarDayLabel>}
        <BookingButtonBackgroud
          selectedDuration={selectedDuration}
          firstDay={isFirstSelected || isFirstDayOfMonth}
          lastDay={isLastSelected || isLastDayOfMonth}
          isLastDayInRow={isLastDayInRow}
          isFirstDayInRow={isFirstDayInRow}
        >
          <BookingButton disabled={isDisabled} selectedDuration={selectedDuration} circle grey={isCheckoutOnly}>
            <CalendarDayLabel>{date}</CalendarDayLabel>
          </BookingButton>
        </BookingButtonBackgroud>
      </BookingButtonContainer>
    </FormDateLabel>
  );
};

export default FormRadioDateButton;
